import type { ReactElement, ReactNode } from 'react';

/**
 * A panel is a flat white block on paper, bounded by a 1px rule.
 *
 * No card shadow, no rounded corners beyond 3px, no elevation language at all.
 * When a panel is the one you are working in, it takes the 3px signal marker on
 * its header — the same marker the navigation uses, so "current" means one
 * thing everywhere in the app.
 */

interface PanelProps {
  readonly title: string;
  /** Nepali or English one-liner under the title. Say what the panel is for. */
  readonly note?: string;
  readonly actions?: ReactNode;
  readonly current?: boolean;
  readonly children: ReactNode;
}

export function Panel({ title, note, actions, current = false, children }: PanelProps): ReactElement {
  return (
    <section className="border border-rule bg-panel">
      {/*
        Stacked on a phone, side by side from `sm` up.

        This was one row with a `shrink-0` action group, which on a 390px
        screen squeezed the title into a two-word column and pushed the last
        button off the right edge. A header that runs off the screen is worse
        than one that takes an extra line.
      */}
      <header
        className={[
          'flex flex-col gap-2 border-b border-rule px-4 py-2.5',
          'sm:flex-row sm:items-start sm:justify-between sm:gap-3',
          current ? 'border-l-[3px] border-l-signal pl-[calc(1rem-3px)]' : '',
        ].join(' ')}
      >
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {note !== undefined && <p className="mt-0.5 text-small text-ink-muted">{note}</p>}
        </div>
        {actions !== undefined && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
        )}
      </header>
      {children}
    </section>
  );
}

/** Padded region for prose and forms. Tables sit outside it, flush to the rules. */
export function PanelBody({ children }: { children: ReactNode }): ReactElement {
  return <div className="px-4 py-4">{children}</div>;
}
